import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { PartyPopper, Loader2, XCircle } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const { refreshUser } = useAuth();
  const [state, setState] = useState("checking");

  useEffect(() => {
    const id = params.get("session_id");
    if (!id) { setState("error"); return; }
    let tries = 0;
    let stopped = false;
    const poll = async () => {
      if (stopped) return;
      try {
        const { data } = await api.get(`/payments/status/${id}`);
        if (["paid", "trial"].includes(data.payment_status)) { await refreshUser(); setState("paid"); return; }
        if (["expired", "failed"].includes(data.payment_status)) { setState("error"); return; }
      } catch { /* retry */ }
      if (++tries >= 40) { setState("timeout"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { stopped = true; };
  }, [params, refreshUser]);

  return (
    <main data-testid="payment-success-page" className="mx-auto max-w-xl px-4 py-24 text-center">
      {state === "checking" && (<><Loader2 size={48} className="mx-auto animate-spin text-[color:var(--terracotta)]" /><h1 className="mt-6 font-display text-3xl font-bold">Nous confirmons votre paiement…</h1></>)}
      {state === "paid" && (
        <div data-testid="payment-confirmed">
          <PartyPopper size={56} className="wobble mx-auto text-[color:var(--terracotta)]" />
          <h1 className="mt-6 font-display text-4xl font-bold">Bienvenue dans Premium !</h1>
          <p className="mt-3 text-lg text-[color:var(--muted-ink)]">Toutes les histoires, la narration et les ateliers sont débloqués.</p>
          <Link to="/bibliotheque" data-testid="payment-go-library" className="btn-primary mt-8">Lire une histoire</Link>
        </div>
      )}
      {(state === "error" || state === "timeout") && (
        <div data-testid="payment-error">
          <XCircle size={48} className="mx-auto text-[color:var(--terracotta)]" />
          <h1 className="mt-6 font-display text-3xl font-bold">{state === "timeout" ? "Le paiement prend un peu de temps" : "Le paiement n'a pas abouti"}</h1>
          <p className="mt-3 text-[color:var(--muted-ink)]">{state === "timeout" ? "Rafraîchissez la page dans un instant." : "Aucun montant n'a été débité."}</p>
          {state === "timeout" && <button data-testid="payment-retry-button" onClick={() => window.location.reload()} className="btn-primary mt-6 mr-3">Vérifier à nouveau</button>}
          <Link to="/premium" className="btn-ghost mt-6">Retour aux formules</Link>
        </div>
      )}
    </main>
  );
}

export function PaymentCancel() {
  return (
    <main data-testid="payment-cancel-page" className="mx-auto max-w-xl px-4 py-24 text-center">
      <h1 className="font-display text-3xl font-bold">Paiement annulé</h1>
      <p className="mt-3 text-lg text-[color:var(--muted-ink)]">Pas de souci, rien n'a été débité. Vous pouvez continuer avec les histoires gratuites.</p>
      <div className="mt-8 flex justify-center gap-3">
        <Link to="/bibliotheque" className="btn-ghost">Bibliothèque</Link>
        <Link to="/premium" data-testid="payment-cancel-retry" className="btn-primary">Revoir les formules</Link>
      </div>
    </main>
  );
}
